// src/app/pages/management/maintain-account/maintain-account.service.ts

import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs'; // Import throwError
import { AjaxService } from '../../../services/ajax.service';
import { ViewScopeService } from '../../../services/view-scope.service';
import { ACCOUNT_API_DEFS, BaseApiDefinition, MAIN_API_DEFS, UPDATE_ACTIVITY, UPDATE_APPROVED, UPDATE_RECORD, DELETE_RECORD, CREATE_RECORD } from '../../../services/account-api.constants';


// Import your models for return types
import { User, ApiUser, DataType } from '../../../models/user.model'; // Added missing imports for models

@Injectable({
	providedIn: 'root'
})
export class MaintainAccountService {
	private apiUrl: string;
	private currentUserIdFromLocalStorage: string | null;
	private selectedEntityTypeId: 100 | 200 = 100;

	constructor(
		private ajaxService: AjaxService,
		private viewScopeService: ViewScopeService
	) {
		this.apiUrl = this.ajaxService.getBaseOfApi();
		this.currentUserIdFromLocalStorage = this.viewScopeService.getCurrentOpertaingUserId().toString();
	}

	private userDataMappings: { key: keyof User; apiName: string, special?: any }[] = [];


	// Define the type of mappings
	private setUserDataMappings() {
		this.userDataMappings = [
			{ key: "additionalNames", apiName: "Additional Names" },
			{ key: "profiles", apiName: "Profilers" },
			{ key: "emails", apiName: "Emails" },
			{ key: "addresses", apiName: "Addresses" },
			{ key: "phoneNumbers", apiName: "Phone Numbers" },
			{ key: "paymentTypes", apiName: "Payment Info" },
			{ key: "relations", apiName: "Relations", special: '/' + this.selectedEntityTypeId },
			{ key: "moveCodes", apiName: "Move Codes" },
			{ key: "moveCode6111s", apiName: "Move Code 6111s" },
			// { key: "moveInPrms", apiName: "Move In Prms" },
			{ key: "urls", apiName: "URLs" },

			{ key: "uploads", apiName: "Uploades" },
		];
	}

	public getMappingIndex(key: keyof User): number {
		return this.userDataMappings.findIndex(m => m.key === key);
	}


	public getUserDataMappings() {
		if (this.userDataMappings == null || this.userDataMappings.length === 0) {
			this.setUserDataMappings();
		}
		return this.userDataMappings;
	}

	public setSelectedEntityTypeId(val: 100 | 200) {
		this.selectedEntityTypeId = val;
	}

	/**
	 * Fetches the main list of customers or suppliers.
	 * @param entityTypeId The ID representing Customer (100) or Supplier (200).
	 * @returns An Observable of ApiUser array.
	 */
	loadAllUsers(entityTypeId: 100 | 200): Observable<ApiUser[]> {
		if (!this.currentUserIdFromLocalStorage) {
			console.error("Error: localStorage 'id' is not available for main user list API call.");
			// Using throwError for consistency with RxJS error handling
			return throwError(() => new Error('Current user ID not found.'));
		}
		const endpoint = `${this.apiUrl}/api/${MAIN_API_DEFS.ALL_CUST_SUP}/${entityTypeId}/${this.currentUserIdFromLocalStorage}/${this.viewScopeService.getSelectedCompanyId()}`;
		console.log(`MaintainAccountService: Loading all users from: ${endpoint}`);

		return this.ajaxService.getData(endpoint) as Observable<ApiUser[]>;
	}

	/**
	 * A generic method to fetch detailed data for a specific user and API type.
	 * This method builds URLs like: {apiUrl}/api/{pathSegment}/{userId}/{currentUserIdFromLocalStorage}
	 * It is NOT suitable for the 'Relations' API as it has a different structure.
	 * @param apiDef The BaseApiDefinition for the desired data type (e.g., Emails, Addresses).
	 * @param userId The ID of the user whose details are being fetched.
	 * @returns An Observable of the expected data type.
	 */
	private fetchUserDetail<T>(apiDef: BaseApiDefinition, userId: string): Observable<T> {
		if (!this.currentUserIdFromLocalStorage) {
			console.error("Error: localStorage 'id' is not available for detail API call.");
			return throwError(() => new Error('Current user ID not found.'));
		}

		// This construct is specifically for APIs that follow the {pathSegment}/{userId}/{currentUserIdFromLocalStorage} pattern.
		const endpoint = `${this.apiUrl}/api/${apiDef.pathSegment}/${userId}/${this.currentUserIdFromLocalStorage}`;

		console.log(`MaintainAccountService: Fetching ${apiDef.name} for user ${userId} from: ${endpoint}`);
		return this.ajaxService.getData(endpoint) as Observable<T>;
	}

	getDatas<T>(userId: number, apiName: string, additions: any): Observable<T[]> {
		const def = ACCOUNT_API_DEFS.find(d => d.name === apiName);
		if (!def) throw new Error(`API definition for "${apiName}" not found.`);

		return this.fetchUserDetail<T[]>(def, userId.toString() + (additions ? additions : ""));
	}


	updateFunctions(funcType: boolean, type: string, recID: number, isActive: 0 | 1) {
		return funcType ? this.updateStatus(type, recID, isActive) : this.updateApproved(type, recID, isActive);
	}

	updateStatus(type: string, recID: number, isActive: 0 | 1) {
		const endpoint = `${this.apiUrl}/api/${this.getStatusPathSegmentByName(type)}`;
		return this.ajaxService.updateObjectData(endpoint, this.createPayload(recID, isActive));
	}

	getStatusPathSegmentByName(name: string): string | undefined {
		return UPDATE_ACTIVITY.find(item => item.name === name)?.pathSegment;
	}

	updateApproved(type: string, recID: number, isActive: 0 | 1) {
		const endpoint = `${this.apiUrl}/api/${this.getApprovedPathSegmentByName(type)}`;
		return this.ajaxService.updateObjectData(endpoint, this.createApprovedPayload(recID, isActive));
	}

	getApprovedPathSegmentByName(name: string): string | undefined {
		return UPDATE_APPROVED.find(item => item.name === name)?.pathSegment;
	}

	deleteRecord(type: string, recID: number) {
		const endpoint = `${this.apiUrl}/api/${this.getDeleteRecordPathSegmentByName(type)}`;
		return this.ajaxService.updateObjectData(endpoint, this.deleteRecordPayload(recID));
	}

	getDeleteRecordPathSegmentByName(name: string): string | undefined {
		return DELETE_RECORD.find(item => item.name === name)?.pathSegment;
	}



	createPayload(recID: number, isActive: 0 | 1) {
		// Construct the payload object
		const payload = {
			"RecID": recID.toString(),
			"IsActive": isActive.toString(),
			"UserRecID": this.viewScopeService.getCurrentOpertaingUserId().toString()
		};
		return JSON.stringify(payload);
	}

	createApprovedPayload(recID: number, isActive: 0 | 1) {
		// Construct the payload object
		const payload = {
			"RecID": recID.toString(),
			"IsApproved": isActive.toString(),
			"UserRecID": this.viewScopeService.getCurrentOpertaingUserId().toString()
		};
		return JSON.stringify(payload);
	}

	deleteRecordPayload(recID: number) {
		// Construct the payload object
		const payload = {
			"RecID": recID.toString(),
			"UserRecID": this.viewScopeService.getCurrentOpertaingUserId().toString()
		};
		return JSON.stringify(payload);
	}




	private updateRecord(type: string, data: any): Observable<any> {
		const path = this.getPathForUpdateByName(type);
		console.log("UDP PATH: " + path);

		if (!path) {
			console.error(`Error: Path not found for type: ${type}`);
			return new Observable(observer => observer.error('Path not found'));
		}
		const fullUrl = this.apiUrl + '/api/' + path;
		return this.ajaxService.updateObjectData(fullUrl, data);
	}

	private insertRecord(type: string, data: any): Observable<any> {
		const path = this.getPathForCreateByName(type);
		if (!path) {
			console.error(`Error: Path not found for type: ${type}`);
			return new Observable(observer => observer.error('Path not found'));
		}
		const fullUrl = this.apiUrl + '/api/' + path;
		return this.ajaxService.addObjectData(fullUrl, data);
	}


	insertOrUpdateRecord(reqType: boolean, type: string, data: any): Observable<any> {
		console.log("Updating DB with operation of: " + reqType);
		console.log("Data we send: " + data)
		return reqType ? this.insertRecord(type, data) : this.updateRecord(type, data);
	}




	getPathForUpdateByName(name: string): string | undefined {
		return UPDATE_RECORD.find(item => item.name === name)?.pathSegment;
	}

	getPathForCreateByName(name: string): string | undefined {
		return CREATE_RECORD.find(item => item.name === name)?.pathSegment;
	}


	updateUserActivity(accRecID: number, accTypeCode: 100 | 200, statusToSet: 0 | 1) {
		const userRecID = this.viewScopeService.getCurrentOpertaingUserId();
		const apiURL = this.apiUrl + '/api/' +  this.getStatusPathSegmentByName(this.constructor.name.substring(1));
		const payload = JSON.stringify({
			"RecID": accRecID,
			"IsActive": statusToSet,
			"CodeType": accTypeCode,
			"UserRecID": userRecID
		});

		// Debug prints
		console.warn('¯\_(ツ)_/¯')
		console.log('updateUserActivity - userRecID:', userRecID);
		console.log('updateUserActivity - apiURL:', apiURL);
		console.log('updateUserActivity - payload:', payload);

		if (!apiURL) return;
		const obs$ = this.ajaxService.updateObjectData(apiURL, payload);
		if (obs$ && typeof obs$.subscribe === 'function') {
			obs$.subscribe({
				next: (res) => {
					console.log('User activity updated successfully!', res);
				},
				error: (err) => {
					console.error('Failed to update user activity:', err);
				}
			});
		}
		return obs$;
	}
}